import { motion } from "framer-motion";
import { X, Download, Maximize, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { API, absoluteShareUrl } from "@/lib/api";

function fileUrl(chart) {
  return `${API}/files/${chart.url.replace("/api/files/", "")}`;
}

export default function Lightbox({ chart, onClose }) {
  const shareUrl = absoluteShareUrl(chart);
  const shareText = `${chart.title} — ${chart.variant_label} · ${chart.category_name}`;

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = fileUrl(chart);
    a.download = `${chart.title}.png`;
    a.target = "_blank";
    a.click();
  };

  const handleScreenshot = async () => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = fileUrl(chart);
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      const footer = 90;
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight + footer;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#050505";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      ctx.fillStyle = "#FFD700";
      ctx.font = "bold 28px sans-serif";
      ctx.fillText(chart.title, 32, img.naturalHeight + 42);
      ctx.fillStyle = "#A1A1A6";
      ctx.font = "22px sans-serif";
      ctx.fillText("via Rocky Agency", 32, img.naturalHeight + 72);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${chart.title}-framed.png`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Framed screenshot downloaded");
      }, "image/png");
    } catch (e) {
      toast.error("Screenshot failed — use Download instead");
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`, "_blank");
  };

  const openTelegram = () => {
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
      "_blank"
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="chart-lightbox"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 p-4 backdrop-blur-2xl"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-black/60 text-white backdrop-blur-xl transition-colors duration-300 hover:border-white/40"
      >
        <X className="h-5 w-5" />
      </button>

      <motion.img
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
        src={fileUrl(chart)}
        alt={chart.title}
        data-testid="lightbox-image"
        className="max-h-[72vh] max-w-full rounded-lg border border-white/10 object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
        className="mt-6 flex items-center gap-3 rounded-full border border-white/10 bg-black/60 px-4 py-3 backdrop-blur-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-action-bar"
      >
        <div className="mr-2 hidden max-w-[200px] md:block">
          <p className="truncate text-sm font-semibold text-white">{chart.title}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1A6]">
            {chart.variant_label} · {chart.category_name}
          </p>
        </div>
        <button
          data-testid="lightbox-download-btn"
          onClick={handleDownload}
          title="Download"
          className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-white transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <Download className="h-5 w-5" />
        </button>
        <button
          data-testid="lightbox-screenshot-btn"
          onClick={handleScreenshot}
          title="Framed screenshot"
          className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-white transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <Maximize className="h-5 w-5" />
        </button>
        <button
          data-testid="lightbox-whatsapp-share"
          onClick={openWhatsApp}
          title="Share on WhatsApp"
          className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-white transition-colors duration-300 hover:bg-[#25D366] hover:text-black"
        >
          <MessageCircle className="h-5 w-5" />
        </button>
        <button
          data-testid="lightbox-telegram-share"
          onClick={openTelegram}
          title="Share on Telegram"
          className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-white transition-colors duration-300 hover:bg-[#229ED9] hover:text-white"
        >
          <Send className="h-5 w-5" />
        </button>
      </motion.div>
    </motion.div>
  );
}
